import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getTokenForRequest } from '../../utils/apiAuth';
import './QuestionReview.css';

/**
 * Helper function to format .NET date string to JavaScript Date
 * @param {string} dotnetDate - .NET date string like "/Date(1234567890)/"
 * @returns {string} - Formatted date string
 */
const formatDotNetDate = (dotnetDate) => {
  if (!dotnetDate) return 'NA';
  
  try {
    // Extract timestamp from .NET date format: /Date(1234567890)/
    const match = dotnetDate.match(/\/Date\((\d+)\)\//);
    if (match) {
      const timestamp = parseInt(match[1]);
      const date = new Date(timestamp);
      // Format to Singapore Time (SGT)
      return date.toLocaleString('en-SG', {
        timeZone: 'Asia/Singapore',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    }
    return 'NA';
  } catch (error) {
    return 'NA';
  }
};

/**
 * Helper function to filter comment content
 * @param {string} content - Comment content
 * @returns {string} - Filtered comment content
 */
const filterComment = (content) => {
  if (!content) return '';
  
  const commentMap = {
    'Published': 'Approved Question',
    'Pending Approval': 'Submitted for Approval',
    'Rejected': 'Rejected by Content Manager',
    'Pre Approved': 'Approved by Content Manager',
    'Pre Rejected': 'Rejected by Question Vetter'
  };
  
  return commentMap[content] || content;
};

/**
 * Helper function to generate GUID using cryptographically secure randomness
 * @returns {string} - GUID string
 */
const generateGuid = () => {
  return crypto.randomUUID();
};

/**
 * Helper function to encode token for API calls
 * @param {string} token - The session token
 * @returns {string} - Encoded token
 */
const encodeToken = (token) => {
  return token.toString();
};

/**
 * QuestionReview Component
 * Displays question review comments and allows adding new comments
 */
const QuestionReview = ({ questionId, token, reviewMode = false }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState({ Content: '', File: '' });
  const [showAllComments, setShowAllComments] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);

  // S3 upload configuration
  const workingFileRoot = 'https://s3-ap-southeast-1.amazonaws.com/uulearner-workings/';
  const policy = 'ewogICJleHBpcmF0aW9uIjogIjIwMjAtMDEtMDFUMDA6MDA6MDBaIiwKICAiY29uZGl0aW9ucyI6IFsKICAgIHsiYnVja2V0IjogInV1bGVhcm5lci13b3JraW5ncyJ9LAogICAgWyJzdGFydHMtd2l0aCIsICIka2V5IiwgIiJdLAogICAgeyJhY2wiOiAicHJpdmF0ZSJ9LAogICAgWyJzdGFydHMtd2l0aCIsICIkQ29udGVudC1UeXBlIiwgIiJdLAogICAgWyJzdGFydHMtd2l0aCIsICIkZmlsZW5hbWUiLCAiIl0sCiAgICBbImNvbnRlbnQtbGVuZ3RoLXJhbmdlIiwgMCwgNTI0Mjg4MDAwXQogIF0KfQ==';
  const signature = 'I2GuTPvlwabTQFVtGZefDACAAIo=';

  // Check URL hash to determine if we're in review mode
  useEffect(() => {
    const checkReviewMode = () => {
      let shouldShow = false;
      
      // Check if reviewMode prop is set
      if (reviewMode === 'Review' || reviewMode === true) {
        shouldShow = true;
        console.log('QuestionReview: Review mode detected from prop:', reviewMode);
      }
      
      // Check URL hash as fallback (always check, not just if prop is false)
      if (window.location.hash) {
        const hash = window.location.hash;
        // Check for patterns like #Questions/Review or #Questions/Review/123
        if (hash.includes('/Review') || hash.includes('Review/') || hash.includes('#Questions/Review')) {
          shouldShow = true;
          console.log('QuestionReview: Review mode detected from URL hash:', hash);
        }
      }
      
      // Also check if we're on a review page by checking the pathname
      if (window.location.pathname && window.location.pathname.includes('review')) {
        shouldShow = true;
        console.log('QuestionReview: Review mode detected from pathname:', window.location.pathname);
      }
      
      console.log('QuestionReview: Setting isReviewMode to:', shouldShow, {
        reviewMode,
        hash: window.location.hash,
        pathname: window.location.pathname
      });
      setIsReviewMode(shouldShow);
    };

    // Check immediately
    checkReviewMode();

    // Also check after a short delay to catch any async updates
    const timeoutId = setTimeout(checkReviewMode, 100);

    // Listen for hash changes
    const handleHashChange = () => {
      checkReviewMode();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [reviewMode]);

  // Fetch comments when questionId changes
  useEffect(() => {
    if (questionId && parseInt(questionId) > 0) {
      getQuestionComments(questionId);
    }
  }, [questionId]);

  /**
   * Fetches question comments from the API
   */
  const getQuestionComments = async (qId) => {
    if (!qId || parseInt(qId) <= 0) return;

    try {
      setIsLoading(true);
      const response = await axios({
        url: './webservices/Question.svc/GetReviewComments',
        method: 'GET',
        params: {
          token: encodeToken(getTokenForRequest()),
          questionId: qId
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        setComments(response.data);
      }
    } catch (error) {
      console.error('Failed to get question comments:', error);
      alert('Failed to get previous question comments.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Adds a new comment
   */
  const addComment = async () => {
    if (!newComment.Content.trim()) {
      alert('You need to write something for the comment');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await axios({
        url: './webservices/Question.svc/AddReviewComment',
        method: 'POST',
        data: {
          token: encodeToken(getTokenForRequest()),
          questionId: questionId,
          comment: newComment.Content,
          file: newComment.File
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      // Reset form
      setNewComment({ Content: '', File: '' });
      
      // Refresh comments
      await getQuestionComments(questionId);
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handles image upload to S3
   */
  const handleImageUpload = async (file) => {
    if (!file) return;

    try {
      setUploadProgress(0);
      
      // Generate unique filename
      const fileExtension = file.name.split('.').pop();
      const filename = `la-qcommentattachment-${generateGuid()}.${fileExtension}`;

      // Get S3 signature from server
      const signatureResponse = await axios({
        url: './webservices/Authentication.svc/GetSignature',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        },
        data: { fileName: filename }
      });

      const retdata = signatureResponse.data;

      // Create form data for S3 upload
      const formData = new FormData();
      formData.append('key', retdata.key || filename);
      formData.append('AWSAccessKeyId', retdata.AWSAccessKeyId || '');
      formData.append('acl', 'private');
      formData.append('policy', retdata.policy || policy);
      formData.append('signature', retdata.signature || signature);
      formData.append('Content-Type', file.type || 'application/octet-stream');
      formData.append('filename', file.name);
      formData.append('file', file);

      // Upload to S3
      // Note: Don't set Content-Type header manually - axios will set it with boundary
      const response = await axios({
        url: 'https://s3-ap-southeast-1.amazonaws.com/uulearner-workings',
        method: 'POST',
        data: formData,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.min(100, Math.round((progressEvent.loaded * 100) / progressEvent.total));
          setUploadProgress(percentCompleted);
        }
      });

      // Update comment with file
      setNewComment(prev => ({ ...prev, File: filename }));
      setUploadProgress(0);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image.');
      setUploadProgress(0);
    }
  };

  /**
   * Handles file input change
   */
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      handleImageUpload(file);
    } else {
      alert('Please select an image file.');
    }
  };

  /**
   * Approves the question
   */
  const approveQuestion = async () => {
    if (!window.confirm('Are you sure you want to approve this question?')) {
      return;
    }

    // If global approveQuestion function exists (from old code), use it
    // Otherwise, call the API directly
    if (window.approveQuestion && typeof window.approveQuestion === 'function') {
      window.approveQuestion();
      return;
    }

    try {
      // For review mode, we need to get the question data first
      // Since we're on the preview page, we'll need to fetch the question
      // For now, we'll use a simplified approach that just calls the approve API
      // Note: The full implementation might need to submit the question first if it was edited
      
      // Get question data from the parent component or fetch it
      const questionResponse = await axios({
        url: `./webservices/Question.svc/Basic?token=${encodeToken(getTokenForRequest())}&questionId=${questionId}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (questionResponse.data) {
        const response = await axios({
          url: './webservices/Question.svc/Approve',
          method: 'POST',
          data: {
            question: questionResponse.data,
            token: encodeToken(getTokenForRequest())
          },
          headers: {
            'Content-Type': 'application/json; charset=utf-8'
          }
        });

        if (response.data) {
          alert('Question is approved successfully.');
          window.location.href = '#Questions/pending';
        }
      }
    } catch (error) {
      console.error('Failed to approve question:', error);
      alert('An error occurred in approving.');
    }
  };

  /**
   * Rejects the question
   */
  const rejectQuestion = async () => {
    if (!window.confirm('Are you sure you want to reject this question?')) {
      return;
    }

    try {
      const response = await axios({
        url: './webservices/Question.svc/Reject',
        method: 'POST',
        data: {
          questionID: questionId,
          comment: '',
          token: encodeToken(getTokenForRequest())
        },
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      if (response.data) {
        alert('Question is rejected successfully.');
        window.location.href = '#Questions/pending';
      }
    } catch (error) {
      console.error('Failed to reject question:', error);
      alert('An error occurred in rejecting question.');
    }
  };

  // Check if user is from SP (currently always returns true)
  const notSP = () => {
    // const ch = new CookieUtilities();
    // if (ch.getCookie('userOrg') === 'Singapore Polytechnic') {
    //   return false;
    // }
    return true;
  };

  // Don't render if user is from SP
  if (!notSP()) {
    return null;
  }

  // Sort comments by time (newest first)
  const sortedComments = [...comments].sort((a, b) => {
    const timeA = a.Time ? parseInt(a.Time.match(/\/Date\((\d+)\)\//)?.[1] || '0') : 0;
    const timeB = b.Time ? parseInt(b.Time.match(/\/Date\((\d+)\)\//)?.[1] || '0') : 0;
    return timeB - timeA; // Descending order (newest first)
  });

  // Determine if we should show approve/reject buttons
  // Check multiple conditions to ensure buttons show when appropriate
  const shouldShowButtons = isReviewMode || 
    reviewMode === 'Review' || 
    reviewMode === true ||
    (typeof window !== 'undefined' && window.location.hash && 
     (window.location.hash.includes('/Review') || window.location.hash.includes('Review/')));

  return (
    <div className="question-review-container">
      {/* Previous Comments */}
      {comments.length > 0 && (
        <div className="row">
          <div className="col-sm-12">
            <h4>Previous Comments</h4>
          </div>
          {comments.length > 1 && (
            <div className="col-sm-12" style={{ paddingBottom: '20px' }}>
              <button 
                className="btn btn-default" 
                onClick={() => setShowAllComments(!showAllComments)}
              >
                Toggle all previous comments
              </button>
            </div>
          )}
        </div>
      )}

      {/* Comments List */}
      <div className="row" style={{ maxHeight: '240px', overflowY: 'auto', overflowX: 'hidden' }}>
        {sortedComments.map((comment, index) => {
          const isLastComment = index === sortedComments.length - 1;
          const shouldShow = isLastComment || showAllComments;
          
          if (!shouldShow || !comment.Content || comment.Content.length === 0) {
            return null;
          }

          return (
            <div 
              key={index} 
              className="row comment-item"
              style={{ borderBottom: '1px solid #DDD', padding: '20px', marginBottom: '10px' }}
            >
              <div className="col-12 col-sm-2">
                <span 
                  style={{
                    padding: '3px',
                    backgroundColor: '#31b0d5',
                    borderRadius: '2px',
                    color: 'white'
                  }}
                >
                  {comment.Role}
                </span>
                <br />
                {formatDotNetDate(comment.Time)}
              </div>
              <div className="col-12 col-sm-10 commenter">
                <div dangerouslySetInnerHTML={{ __html: filterComment(comment.Content) }} />
                {comment.File && comment.File.length > 0 && (
                  <div>
                    <img 
                      style={{ maxWidth: '100%', border: '1px solid black', marginTop: '10px' }}
                      src={`${workingFileRoot}${comment.File}`}
                      alt="Comment attachment"
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add Comment Form */}
      <div className="row form" style={{ marginTop: '20px' }}>
        <div className="col-12 col-sm-6">
          <div className="form-group">
            <label>New Comment</label>
            <textarea 
              className="form-control question-review-textarea" 
              value={newComment.Content}
              onChange={(e) => setNewComment(prev => ({ ...prev, Content: e.target.value }))}
              rows="8"
            />
          </div>
          <div className="form-group question-review-buttons">
            {newComment.File && (
              <div style={{ marginBottom: '10px', color: '#666' }}>
                Uploaded: {newComment.File}
                <span
                  className="glyphicon glyphicon-remove"
                  style={{ cursor: 'pointer', marginLeft: '10px', color: '#d9534f' }}
                  onClick={() => setNewComment(prev => ({ ...prev, File: '' }))}
                  title="Remove file"
                ></span>
              </div>
            )}
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="progress" style={{ marginTop: '10px', marginBottom: '10px' }}>
                <div 
                  className="progress-bar" 
                  role="progressbar" 
                  style={{ width: `${uploadProgress}%` }}
                >
                  {uploadProgress}%
                </div>
              </div>
            )}
            <div className="question-review-button-group">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="image-upload-input"
              />
              <label htmlFor="image-upload-input" className="btn btn-primary question-review-upload-btn">
                Upload Image
              </label>
              <button 
                className="btn btn-success question-review-add-btn" 
                onClick={addComment}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Adding...' : 'Add Comment'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Review Mode Actions - Show buttons if in review mode or URL indicates review */}
      {shouldShowButtons && (
        <div className="row" style={{ paddingBottom: '40px', marginTop: '20px' }}>
          <div className="col-sm-12">
            <span style={{ color: 'red' }}>
              Please do not reject question after edit question
            </span>
          </div>
          <div className="col-sm-12" style={{ marginTop: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              className="btn btn-success" 
              onClick={approveQuestion}
              type="button"
            >
              Approve Question
            </button>
            <button 
              className="btn btn-danger" 
              onClick={rejectQuestion}
              type="button"
            >
              Reject Question
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionReview;

